<template>
  <v-container v-resize="onResize">
    <GoBackAtTop :to="{name: 'CompanyApplications'}" label="Lärlingsanmälningar" />
    <div class="title-icon mb-n2" v-if="windowSize.x > 400">
      <h1>{{ $t('labels.application2') }}</h1>
      <v-btn
        icon
        href="https://www.byggbranschensyrkesnamnd.se/guide/foretag-steg4-bekrafta-larlingsanmalan/"
        target="_blank"
      >
        <v-icon>$sl-question</v-icon>
      </v-btn>
    </div>
    <div v-else>
      <div class="d-flex justify-end">
        <v-btn
          class="mt-n10"
          icon
          width="32"
          href="https://www.byggbranschensyrkesnamnd.se/guide/foretag-steg4-bekrafta-larlingsanmalan/"
          target="_blank"
        >
          <v-icon>$sl-question</v-icon>
        </v-btn>
      </div>
      <h1 class="pt-4 mb-2">{{ $t('labels.application2') }}</h1>
    </div>
    <span class="gray"
      >{{ $t('labels.created') }} {{ application.createdAt | date }}
      {{ $t('ebook.education-approvebox2-text-clock') }}
      {{ application.createdAt | clocktime }}
      {{ $t('labels.of') }} {{ displayName }}.
    </span>

    <MessageBox class="mt-5 mb-6" type="info" v-if="application.transferOldProfession" :closable="true">
      <p v-html="$t('application.transfer.explanation', { 
        name: `${ application.firstName } ${ application.lastName }` 
      })"></p>
    </MessageBox>

    <template v-else>
      <MessageBox class="mt-5 mb-6" type="alert" v-if="application.professionId == 8" :closable="true">
        <p v-html="$t('company.review.application.messageBox-p1')"></p>
      </MessageBox>
      
      <MessageBox class="mt-5 mb-6" type="info" :closable="false" v-if="application.allCoursesApproved">
        <p v-html="$t('company.review.application.messageBox-p4')"></p>
      </MessageBox>
      
      <MessageBox class="mt-5 mb-6" type="info" :closable="false" v-else>
        <p v-html="$t('company.review.application.messageBox-p5')"></p>
      </MessageBox>
    </template>


    <MessageBox
      class="mt-3 mb-6"
      type="info"
      :closable="true"
      @closed="setCookie('reviewApplicationInfo')"
      v-if="$cookies.get('reviewApplicationInfo') != currentUser.id && currentUser.id && !application.transferOldProfession"
    >
      <h6>{{ $t('company.review.application.messageBox-p3-title') }}</h6>
      <p v-html="$t('company.review.application.messageBox-p3')"></p>
    </MessageBox>
    <MessageBox
      class="mt-3 mb-6"
      type="info"
      :closable="true"
      @closed="setCookie('reviewApplicationStep1')"
      v-if="$cookies.get('reviewApplicationStep1') != currentUser.id && currentUser.id"
    >
      <p v-html="$t('company.review.application.messageBox-step1')"></p>
    </MessageBox>

    <v-text-field
      class="disabled-no-outlined mt-4"
      v-if="application"
      :label="$t('fields.name')"
      v-model="displayName"
      disabled
    ></v-text-field>
    <v-text-field
      v-if="application && application.professionId"
      class="mt-n2 disabled-no-outlined"
      :label="$t('fields.apprentice-profession')"
      v-model="professionName"
      disabled
    ></v-text-field>
    <v-text-field
      v-if="application && application.professionId && hasProfessionOrientations(application.professionId)"
      class="mt-n2 disabled-no-outlined"
      :label="$t('fields.machine-orientation')"
      v-model="orientations"
      disabled
    ></v-text-field>
    <v-text-field
      v-if="application && application.stateId && application.state.state"
      class="mt-n2 prepend-icons disabled-no-outlined"
      :label="$t('labels.status')"
      v-model="statusName"
      :prepend-icon="applicationIconByName(application.state.state)"
      disabled
    ></v-text-field>

    <h4 class="py-2">{{ $t('labels.document2') }}</h4>
    <MessageBox
      class="mt-3 mb-6"
      type="info"
      :closable="true"
      @closed="setCookie('reviewApplicationStep2')"
      v-if="$cookies.get('reviewApplicationStep2') != currentUser.id && currentUser.id"
    >
      <p v-html="$t('company.review.application.messageBox-step2')"></p>
    </MessageBox>

    <div v-if="gradeDocs && gradeDocs.length > 0">
      <p class="overline mb-1">Betyg</p>
      <div v-for="c in gradeDocs" :key="c.id">
        <div class="d-flex align-center">
          <v-icon>$sl-file</v-icon>
          <div class="document">
            <a v-if="c && c.fileName && c.internalFileName" @click="getDocument(c)">
              {{ c.fileName }}
              <v-icon class="color-grey" size="16">$sl-window-expand</v-icon>
            </a>
          </div>
        </div>
      </div>
      <div class="mt-1" v-if="gradeDocsShcoolText">
        {{gradeDocsShcoolText}}
      </div>
    </div>

    <div class="mt-5" v-if="certificateDocs && certificateDocs.length > 0">
      <p class="overline mb-1">Intyg</p>
      <div v-for="c in certificateDocs" :key="c.id">
        <div class="d-flex align-center">
          <v-icon>$sl-file</v-icon>
          <div class="document">
            <a v-if="c && c.fileName && c.internalFileName" @click="getDocument(c)">
              {{ c.fileName }}
              <v-icon class="color-grey" small>$sl-window-expand</v-icon>
            </a>
          </div>
        </div>
      </div>
    </div>

    <p v-if="gradeDocs && gradeDocs.length === 0 && certificateDocs && certificateDocs.length === 0">
      {{ $t('company.review.application.no-courses') }}
    </p>

    <h4 class="mt-10 mb-8">{{ $t('company.review.application.courses-title') }}</h4>

    <MessageBox
      class="mt-3 mb-6"
      type="info"
      :closable="true"
      @closed="setCookie('reviewApplicationStep3')"
      v-if="$cookies.get('reviewApplicationStep3') != currentUser.id && currentUser.id"
    >
      <p v-html="$t('company.review.application.messageBox-step3')"></p>
    </MessageBox>

    <p v-html="$t('company.review.application.courses-text2')"></p>

    <p v-if="currentTCs" v-html="$t('company.review.application.courses-text4', {name: displayName, tc: currentTCs, profession: professionName, link: links.application.reviewWithWidening})"></p>

    <p v-if="providerHasCourses" 
      v-html="$t('company.review.application.courses-text3')"></p>

    <ul class="application__courses pt-2">
      <template v-for="(ac, index) in selectedApplication.applicationCourses">
        <li
          v-if="ac.course && ac.course.courseName && ac.approved && ac.approved == true"
          class="d-flex justify-space-between bg-green"
          :key="index"
        >
          <div class="d-flex align-center">
            <v-icon> $sl-check1 </v-icon>
            <div>{{ printCourseName(ac.course) }}</div>
          </div>
          <div>{{ ac.course.courseCode }}</div>
        </li>
        <li v-else-if="ac.course && ac.course.courseName" class="d-flex justify-space-between bg-red" :key="index">
          <div class="d-flex align-center">
            <div>
              <v-icon>$sl-cancel2</v-icon>
            </div>
            <div class="mld-3">{{ printCourseName(ac.course) }}</div>
          </div>
          <div class="pl-1">{{ ac.course.courseCode }}</div>
        </li>
      </template>
    </ul>

    <div class="mt-5 mb-10">
      <p class="overline small">{{ $t('company.explanation') }}</p>
      <p class="mt-n2">
        <v-icon class="mr-2" style="padding: 3px">$sl-check1</v-icon>{{ $t('company.review.application.courses-exp1') }}
      </p>
      <p class="mt-n6">
        <v-icon class="mr-2" style="padding: 1px">$sl-cancel2</v-icon
        >{{ $t('company.review.application.courses-exp3') }}
      </p>
    </div>

    <AdminApplicationData
      class="ma-0 pa-0"
      ref="child"
      :editable="menuActive"
      @setCookie="setCookie"
      :allCoursesApproved="selectedApplication.allCoursesApproved"
      v-if="selectedApplication && selectedApplication.id"
    />
    
    <p class="pt-4 mb-n2" v-if="!allDataSaved">{{ $t('company.review.application.save-text') }}</p>

    <ActionButton
      flat
      class="mt-6"
      @click="canApprove()"
      actionIcon="$sl-like"
      v-bind:class="{disabled: !menuActive}"
      :isDisabled="!menuActive || !allDataSaved || isImitatingUser()"
      >{{ $t('labels.btnActionApprove') }}</ActionButton
    >
    <ActionButton
      flat
      type="application"
      class="mt-6"
      @click="
        modalType = 'deny'
        openModal = true
      "
      actionIcon="$sl-stop"
      :isDisabled="!menuActive || isImitatingUser()"
      v-bind:class="{disabled: !menuActive}"
      >{{ $t('labels.btnActionDisslike') }}</ActionButton
    >
    <ActionButton
      flat
      type="application"
      class="mt-6"
      @click="
        modalType = 'delete'
        openModal = true
      "
      actionIcon="$sl-delete"
      :isDisabled="!menuActive || isImitatingUser()"
      v-bind:class="{disabled: !menuActive}"
      >{{ $t('labels.btnActionDelete') }}</ActionButton
    >

    <AdminApplicationModal
      :show="openModal"
      @close="openModal = false"
      :application="application"
      :type="modalType"
    ></AdminApplicationModal>
  </v-container>
</template>

<script>
import API from '@/services/api.js'
import {mapActions, mapGetters} from 'vuex'
import MessageBox from '@/components/Common/MessageBox'
import ActionButton from '@/components/Common/ActionButton'
import GoBackAtTop from '@/components/Common/GoBackAtTop'
import AdminApplicationData from '@/components/Company/AdminApplicationData'
import AdminApplicationModal from '@/components/Company/AdminApplicationModal'
import DialogWithCheckbox from '@/components/Common/DialogWithCheckbox.vue'
import pick from 'lodash/pick'
import get from 'lodash/get'
import filter from 'lodash/filter'
import {Icons, CompanyRoles} from '@/components/mixins'
// @group Company
// ReviewApplication component
export default {
  name: 'ReviewApplication',

  mixins: [Icons, CompanyRoles],

  data: () => ({
    links: require('../../lib/external-links.json'),
    modalType: '',
    windowSize: {
      x: 0,
      y: 0
    },
    appStatusIcon: '',
    openModal: false,
    application: {},
    fields: [
      'id',
      'allCoursesApproved',
      'firstName',
      'lastName',
      'stateId',
      'state',
      'professionId',
      'createdAt',
      'applicationDocuments',
      'person',
      'transferOldProfession'
    ],
    valid: true
  }),
  components: {
    MessageBox,
    AdminApplicationData,
    ActionButton,
    GoBackAtTop,
    AdminApplicationModal
  },

  computed: {
    ...mapGetters('currentuser', ['currentUser', 'isCompanyUser', 'isImitatingUser']),
    ...mapGetters('application', ['selectedApplication', 'currentStep', 'isValidToSubmit']),
    ...mapGetters('basic', [
      'basicStates',
      'getStateNameById',
      'getProfessionNameById',
      'getProfessionById',
      'toApproveByCompanyStatesId',
      'basicProfessions',
      'hasProfessionOrientations',
    ]),

    gradeDocsShcoolText(){
      if(this.selectedApplication.school){
        const { region, municipality, name } = this.selectedApplication.school
        return `${region} - ${municipality} - ${name}`
      }
      if(this.selectedApplication.anotherSchool){
        return this.$t('application.documents.another-school-review')
      }
      return ''
    },

    providerHasCourses() {
      const profession = this.getProfessionById(this.selectedApplication.professionId)
      return get(profession, 'distanceEducationProvider.features.hasCourses',false)
    },

    allDataSaved() {
      if (
        this.selectedApplication &&
        this.selectedApplication.instructorId > 0 &&
        this.selectedApplication.firstName &&
        this.selectedApplication.lastName &&
        this.selectedApplication.personalNumber &&
        this.selectedApplication.mobilephone &&
        this.selectedApplication.email &&
        this.selectedApplication.address1 &&
        this.selectedApplication.zip &&
        this.selectedApplication.city &&
        this.selectedApplication.workplaceId > 0 &&
        this.selectedApplication.employmentTypeId > 0 &&
        this.selectedApplication.employmentDate
      ) {
        if (this.hasDepartments(this.$route.params.id) === true && this.application.departmentId<1) {
          return false
        }
        return true
      }
      return false
    },
    /* Used to filter betyg docs */
    gradeDocs() {
      return get(this, 'application.applicationDocuments', [])
    },
    certificateDocs() {
      const docs = get(this.application, 'applicationDocuments', [])
      return filter(docs, ['applicationDocumentType.name', 'Övrigt intyg'])
    },

    /* Used to filter intyg docs */
    menuActive() {
      return this.toApproveByCompanyStatesId.includes(this.selectedApplication.stateId)
    },
    /**
     * @vuese
     * Used to display full name
     */
    displayName() {
      if (!this.application) {
        return ''
      }
      return (
        (this.application.firstName ? this.application.firstName : '') +
        ' ' +
        (this.application.lastName ? this.application.lastName : '')
      )
    },
    /**
     * @vuese
     * Used to get profession name by id
     */
    professionName() {
      if (!this.application && this.application.professionId) {
        return ''
      }
      return this.getProfessionNameById(this.application.professionId)
    },
    orientations() {
      const professionOrientations = this.selectedApplication.professionOrientations
      return professionOrientations.map(orientation => orientation.professionMachineOrientation.name).join(', ')
    },
    /**
     * @vuese
     * Used to get status name by state id
     */
    statusName() {
      if (!this.application && this.application.stateId) {
        return ''
      }
      return this.getStateNameById(this.application.stateId)
    },

    currentTCs() {
      const tcs = get(this, 'application.person.tradeCertificates', [])
      if (tcs) {
        return tcs.map(tc => get(tc, 'profession.name', 'Yrke')).join(', ')
      }
      return null
    }

  },

  watch: {
    /**
     * @vuese
     * Used to watch route ,  and set application to empty value {}
     * @arg called with no argument
     */
    $route(to, from) {
      // this.setSelectedApplication({})
    }
  },
  created() {
    this.setSelectedApplication({})
    this.initApplication()
  },

  mounted() {
    this.onResize()
    this.$scrollToTop()
  },

  methods: {
    ...mapActions('application', ['setSelectedApplication']),
    onResize() {
      this.windowSize = {x: window.innerWidth, y: window.innerHeight}
    },
    /**
     * @vuese
     * Used to Initialize application with either selected fields or apply empty fields if none selected
     */
    initApplication() {
      if (this.isCompanyUser(this.$route.params.id) && this.isCompanyUser(this.$route.params.id) == true) {
        if (this.selectedApplication && this.selectedApplication.id) {
          this.application = pick(this.selectedApplication, this.fields)
        } else {
          //- Get application
          API.getApplication(this.$route.params.application_id)
            .then(response => {
              this.setSelectedApplication(response.data)
              this.application = pick(this.selectedApplication, this.fields)
            })
            .catch(error => {
              console.log('There was an error:', error)
            })
        }
      } else {
        this.$router.push('/').catch(() => {})
      }
    },

    /**
     * @vuese
     * Used to return modal type approve and open the modal
     */
    setCookie(keyName) {
      this.$cookies.set(keyName, this.currentUser.id, '2m')
    },
    canApprove() {
      let valids = []
      //- Validate the form
      valids.push(this.$refs.child.$refs.form.validate())

      //- Validate data
      valids.push(this.isValidToSubmit(this.selectedApplication.id))

      if (valids.every(value => value === true)) {
        this.modalType = 'approve'
        this.openModal = true
      } else {
        this.$events.fire('showError', this.$t('errors.application'))
      }
    },
    async getDocument(doc) {
      const shouldSkipDownloadConfirmation = $cookies.get('shouldSkipDownloadConfirmation')
      if (!shouldSkipDownloadConfirmation) {
        const result = await this.$dialog.showAndWait(DialogWithCheckbox, {
          title: this.$t('modals.sensitive-information.title'),
          subtitle: this.$t('modals.sensitive-information.subtitle'),
          text: this.$t('modals.sensitive-information.text'),
          label: this.$t('modals.sensitive-information.label'),
          width: '576',
          showClose: false,
          actions: [
            {text: this.$t('modals.sensitive-information.action-confirm'), key: true, icon: '$sl-check-circle'},
            {text: this.$t('labels.cancel'), key: false, icon: '$sl-cancel', outlined: true}
          ]
        })
        if (!(result && result.data)) return
        if (result.checkbox) $cookies.set('shouldSkipDownloadConfirmation', true, '2m')
      }

      API.getApplicationDocuments(doc.id)
        .then(response => {
          const file = new Blob([response.data], {type: get(response,"headers['content-type']",'application/pdf')})
          const fileURL = URL.createObjectURL(file)
          window.open(fileURL, '_blank')
        })
        .catch(error => {
          console.log('get document error, ' + doc, error)
        })
    },

    printCourseName(course) {
     return get(course, 'use_this_text_instead_of_orderitem_on_order', null) || get(course, 'courseName', '')
    },
  }
}
</script>

<style scoped lang="scss"></style>
